import { useEffect } from "react";

const SITE = "https://bigwind.surf";
const DEFAULT_TITLE =
  "Big Wind — Wind forecasts for kiters, wingfoilers and windsurfers";
const DEFAULT_DESCRIPTION =
  "Spots and 7-day wind forecasts for kiters, wingfoilers and windsurfers across Sweden.";

export interface MetaOptions {
  /** Full page title. Falls back to the homepage default. */
  title?: string;
  /** Meta description. Falls back to the homepage default. */
  description?: string;
  /** Path-relative URL (e.g. "/spot/skanor"). Combined with the site
   *  origin to form the canonical / og:url. Defaults to "/". */
  path?: string;
}

/** Sets document.title, meta[description], link[canonical], og:url,
 *  og:title, og:description, twitter:title, twitter:description for the
 *  current route. og:image / twitter:image are not touched — every route
 *  shares the same static /assets/og-image.png from index.html. Restores
 *  the previous values on unmount so route transitions can't leave stale
 *  tags hanging around if the next page forgets to call useMeta. */
export function useMeta(opts: MetaOptions) {
  const title = opts.title ?? DEFAULT_TITLE;
  const description = opts.description ?? DEFAULT_DESCRIPTION;
  const url = `${SITE}${opts.path ?? "/"}`;

  useEffect(() => {
    const prev = {
      title: document.title,
      description: getMeta("name", "description"),
      ogTitle: getMeta("property", "og:title"),
      ogDescription: getMeta("property", "og:description"),
      ogUrl: getMeta("property", "og:url"),
      twTitle: getMeta("name", "twitter:title"),
      twDescription: getMeta("name", "twitter:description"),
      canonical: getCanonical(),
    };

    document.title = title;
    setMeta("name", "description", description);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", description);
    setMeta("property", "og:url", url);
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", description);
    setCanonical(url);

    return () => {
      document.title = prev.title;
      setMeta("name", "description", prev.description);
      setMeta("property", "og:title", prev.ogTitle);
      setMeta("property", "og:description", prev.ogDescription);
      setMeta("property", "og:url", prev.ogUrl);
      setMeta("name", "twitter:title", prev.twTitle);
      setMeta("name", "twitter:description", prev.twDescription);
      setCanonical(prev.canonical);
    };
  }, [title, description, url]);
}

function getMeta(attr: "name" | "property", key: string): string {
  const el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  return el?.content ?? "";
}

function setMeta(attr: "name" | "property", key: string, value: string) {
  let el = document.head.querySelector<HTMLMetaElement>(
    `meta[${attr}="${key}"]`,
  );
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.content = value;
}

function getCanonical(): string {
  const el = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  return el?.href ?? "";
}

function setCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
}
